/**
 * The section pills (task 201), sized to the room they have.
 *
 * Until 2026-10-08 the header was one row of nine pills with
 * `overflow-x: auto`, which fit above about 1600px and scrolled sideways
 * everywhere else. This component measures instead: a hidden copy of
 * every pill plus the More pill is laid out off-screen, a ResizeObserver
 * watches the nav's width, and as many pills as fit are shown. The rest
 * fold into a More menu, last first, so the pipeline order (task 158:
 * what we connect to, then what came in, then what was noticed, then
 * what needs a person, then who for) survives the fold. If the active
 * section is folded the More pill takes the active style, so where you
 * are is always on the bar. It still says only "More": the section's
 * name is the breadcrumb in row two, and "More · Tickets" read as a
 * broken label (Steve, 2026-10-12).
 *
 * The roadmap sections (Investigation, Settings) are not pills any more.
 * They sit greyed at the foot of the More menu, which exists only while
 * something is folded; the account menu carries Settings as well, so the
 * signal survives on a wide screen without costing the bar any width.
 *
 * The active tab is decided from the router state (Layout passes
 * `pathname`), never from TanStack's `activeProps`, whose classes merged
 * with the inactive ones and let Tailwind's cascade decide.
 */

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';

import { MenuDivider, MenuPanel, SoonItem, menuItemClass, menuItemCurrentClass } from './MenuPanel';

export interface SectionTab {
  to: string;
  label: string;
  exact?: boolean;
  /** A count worn on the pill: the unseen raised alerts on Alerts (Story 25). */
  badge?: string | null;
}

export interface SectionNavProps {
  tabs: SectionTab[];
  /** Roadmap entries, shown greyed under the folded sections. */
  soon?: string[];
  pathname: string;
}

const GAP = 6;

export function isActiveSection(pathname: string, to: string, exact?: boolean): boolean {
  if (exact) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function SectionNav({ tabs, soon = [], pathname }: SectionNavProps) {
  const navRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const [moreOpen, setMoreOpen] = useState(false);

  const fit = useCallback(() => {
    const nav = navRef.current;
    const row = measureRef.current;
    if (!nav || !row) return;
    const pills = Array.from(row.children) as HTMLElement[];
    // The last child of the measuring row is the More pill.
    const moreWidth = (pills[pills.length - 1]?.offsetWidth ?? 0) + GAP;
    const widths = pills.slice(0, tabs.length).map((p) => p.offsetWidth + GAP);
    const available = nav.clientWidth;
    const total = widths.reduce((a, b) => a + b, 0);
    let count = tabs.length;
    if (total > available) {
      count = 0;
      let used = 0;
      for (const w of widths) {
        if (used + w + moreWidth > available) break;
        used += w;
        count += 1;
      }
      // Overview always stays on the bar: with nothing but More the bar
      // would say nothing about where you are.
      count = Math.max(count, 1);
    }
    setVisibleCount((prev) => (prev === count ? prev : count));
  }, [tabs.length]);

  useLayoutEffect(() => {
    fit();
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(nav);
    // Lexend arrives after first paint; the pills get wider when it does.
    const fonts = document.fonts;
    if (fonts?.ready) void fonts.ready.then(fit);
    return () => ro.disconnect();
  }, [fit]);

  // Navigating is how the menu is used; arriving closes it.
  useLayoutEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  const shown = tabs.slice(0, visibleCount);
  const folded = tabs.slice(visibleCount);
  const foldedActive = folded.find((t) => isActiveSection(pathname, t.to, t.exact));
  const closeMore = useCallback(() => setMoreOpen(false), []);

  return (
    <nav ref={navRef} className="relative ml-3 min-w-0 flex-1" aria-label="Sections">
      {/* The measuring row: every pill at natural width, never seen. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-1.5"
      >
        {tabs.map((t) => (
          <span key={t.to} className={pillClass(false)}>
            <PillBody label={t.label} badge={t.badge} />
          </span>
        ))}
        <span className={pillClass(false)}>
          <PillBody label="More" />
          <Chevron />
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        {shown.map((t) => {
          const active = isActiveSection(pathname, t.to, t.exact);
          return (
            <Link
              key={t.to}
              to={t.to}
              className={pillClass(active)}
              aria-current={active ? 'page' : undefined}
            >
              <PillBody label={t.label} badge={t.badge} />
            </Link>
          );
        })}

        {folded.length > 0 && (
          <div className="relative">
            <button
              ref={moreRef}
              type="button"
              className={pillClass(Boolean(foldedActive))}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              aria-current={foldedActive ? 'page' : undefined}
              onClick={() => setMoreOpen((o) => !o)}
            >
              <PillBody label="More" />
              <Chevron />
            </button>
            <MenuPanel
              open={moreOpen}
              onClose={closeMore}
              triggerRef={moreRef}
              label="More sections"
            >
              {folded.map((t) => {
                const active = isActiveSection(pathname, t.to, t.exact);
                return (
                  <Link
                    key={t.to}
                    to={t.to}
                    role="menuitem"
                    className={`${menuItemClass} ${active ? menuItemCurrentClass : ''}`}
                    aria-current={active ? 'page' : undefined}
                  >
                    {t.label}
                    {t.badge && <Badge value={t.badge} />}
                  </Link>
                );
              })}
              {soon.length > 0 && <MenuDivider />}
              {soon.map((label) => (
                <SoonItem key={label} label={label} />
              ))}
            </MenuPanel>
          </div>
        )}
      </div>
    </nav>
  );
}

// One class set per state, never merged (the activeProps lesson).
function pillClass(active: boolean): string {
  const base =
    'inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-sm ' +
    'leading-none transition-colors focus-visible:outline-hidden focus-visible:ring-2 ' +
    'focus-visible:ring-bluesphere-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-bluesphere-bg';
  return active
    ? `${base} bg-white font-semibold text-bluesphere-primary shadow-md`
    : `${base} bg-white/10 font-medium text-white hover:bg-white/20`;
}

function PillBody({ label, badge }: { label: string; badge?: string | null }) {
  return (
    <>
      <span>{label}</span>
      {badge && <Badge value={badge} />}
    </>
  );
}

function Badge({ value }: { value: string }) {
  return (
    <span
      className="rounded-full bg-bluesphere-scoreCritical px-1.5 py-0.5 text-[10px] font-bold leading-none text-white"
      aria-label={`${value} unseen`}
    >
      {value}
    </span>
  );
}

export function Chevron() {
  return (
    <svg
      aria-hidden="true"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 3.5 5 6.5 8 3.5" />
    </svg>
  );
}
